import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Info } from 'lucide-react';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const fmtCurrency = (val) => {
  if (val == null || Number.isNaN(val)) return '—';
  return val.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
};

const PALETTE = ['#7F1D1D', '#B91C1C', '#DC2626', '#EF4444', '#F87171'];

/**
 * Mini grafico orizzontale "Top voci di costo" da affiancare alla sezione Redditività.
 * Mostra fino a `limit` categorie ordinate per importo decrescente.
 */
export default function TopCostsChart({ profitability, limit = 5, title = 'Top voci di costo', testId = 'top-costs-chart' }) {
  const data = useMemo(() => {
    const breakdown = profitability?.cost_breakdown || {};
    const types = profitability?.cost_category_types || {};
    return Object.entries(breakdown)
      .map(([category, amount]) => ({ category, amount, type: types[category] }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, limit);
  }, [profitability, limit]);

  const totalCosts = profitability?.total_costs || 0;
  const netRevenue = (profitability?.revenue || 0) * 0.78;

  if (data.length === 0) {
    return (
      <TooltipProvider delayDuration={150}>
      <div className="bg-white border border-slate-200 p-5" data-testid={testId}>
        <h3 className="font-heading text-base font-semibold mb-2">{title}</h3>
        <p className="text-sm text-slate-400 italic mt-6 text-center">Nessun costo registrato nel periodo.</p>
      </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
    <div className="bg-white border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-heading text-base font-semibold flex items-center gap-1.5">
          {title}
          <UITooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex opacity-40 hover:opacity-100 focus:outline-none" aria-label="Info top voci di costo">
                <Info className="w-3 h-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <span className="font-medium">Top voci di costo</span>: le {limit} categorie con importo più alto del periodo selezionato. Tooltip sulle barre per le percentuali sui ricavi netti e sul totale costi.
            </TooltipContent>
          </UITooltip>
        </h3>
        <span className="text-xs text-slate-400">
          {data.length} di {Object.keys(profitability?.cost_breakdown || {}).length}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 44)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 28, top: 4, bottom: 4 }}>
          <XAxis type="number" hide domain={[0, 'dataMax']} />
          <YAxis
            type="category"
            dataKey="category"
            width={130}
            tick={{ fontSize: 11, fill: '#475569' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(220, 38, 38, 0.06)' }}
            content={({ active, payload }) => {
              if (!active || !payload || !payload.length) return null;
              const row = payload[0].payload;
              const pctNet = netRevenue > 0 ? (row.amount / netRevenue) * 100 : null;
              const pctTotal = totalCosts > 0 ? (row.amount / totalCosts) * 100 : null;
              return (
                <div className="bg-white border border-slate-200 px-3 py-2 shadow-lg text-xs">
                  <p className="font-semibold text-slate-900 mb-1">{row.category}</p>
                  <p className="font-mono text-slate-700">{fmtCurrency(row.amount)}</p>
                  {pctNet != null && (
                    <p className="text-red-700 mt-1">
                      <span className="font-semibold">{pctNet.toFixed(1)}%</span>
                      <span className="text-slate-500"> sui ricavi netti</span>
                    </p>
                  )}
                  {pctTotal != null && (
                    <p className="text-slate-500">
                      <span className="font-semibold text-slate-700">{pctTotal.toFixed(1)}%</span> del totale costi
                    </p>
                  )}
                </div>
              );
            }}
          />
          <Bar dataKey="amount" radius={[0, 0, 0, 0]} barSize={22}>
            {data.map((d, idx) => (
              <Cell key={d.category} fill={PALETTE[idx % PALETTE.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500 border-t border-slate-100 pt-3">
        <span className="flex items-center gap-1.5">
          Totale costi periodo
          <UITooltip>
            <TooltipTrigger asChild>
              <button type="button" className="inline-flex opacity-40 hover:opacity-100 focus:outline-none" aria-label="Formula totale costi">
                <Info className="w-3 h-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <span className="font-medium">Totale costi</span>: somma di tutte le voci di costo del periodo (ricorrenti + una tantum + miste).
            </TooltipContent>
          </UITooltip>
        </span>
        <span className="font-mono font-semibold text-slate-800">{fmtCurrency(totalCosts)}</span>
      </div>
      {netRevenue > 0 && (
        <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
          <span className="flex items-center gap-1.5">
            Incidenza sui ricavi netti
            <UITooltip>
              <TooltipTrigger asChild>
                <button type="button" className="inline-flex opacity-40 hover:opacity-100 focus:outline-none" aria-label="Formula incidenza">
                  <Info className="w-3 h-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <span className="font-medium">Incidenza</span>: Totale costi ÷ Ricavi netti × 100.
              </TooltipContent>
            </UITooltip>
          </span>
          <span className="font-medium">{((totalCosts / netRevenue) * 100).toFixed(1)}%</span>
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
